import { Inject, Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject, Subject, zip } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import {
  AppType,
  FieldMask,
  MarketingInformation,
  MarketplaceApp,
  MarketplaceAppService as MarketplaceAppSDKService,
} from '@galaxy/marketplace-apps/v1';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  Addon,
  AddonService,
  AppEnablementStatus,
  AppType as MarketplacePackagesAppType,
  ENUM_TO_LMI_ARRAY,
  MarketplaceAppService as MarketplacePackagesAppService,
  PartnerAppEnablementStatusService,
} from '@vendasta/marketplace-packages';
import { File, Product as frontendProduct, TrialConfigurationInterface } from '@vendasta/store';
import { AppConfigService } from '../app-config.service';
import { AppIDs, GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES } from '../constants';
import { VConfigService } from '../vconfig/vconfig.service';
import { MarketplaceApp as InternalMarketplaceApp } from './marketplace-app/marketplace-app';
import { MarketsService } from './markets/markets.service';
import { PartnerService } from './partner.service';
import { ProductChangeService } from './product-change.service';

@Injectable({ providedIn: 'root' })
export class MarketplaceAppService implements OnDestroy {
  unsubscribe$ = new Subject();

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private marketPlaceApiService: MarketplaceAppSDKService,
    private enablementService: PartnerAppEnablementStatusService,
    private productChangeService: ProductChangeService,
    private addonService: AddonService,
    private snackbarService: SnackbarService,
    private translationService: TranslateService,
    private partnerService?: PartnerService,
    private marketsService?: MarketsService,
    private vconfigService?: VConfigService,
    private configService?: AppConfigService,
    private pkgAppsService?: MarketplacePackagesAppService,
  ) {
    this.loadAllMarketplaceApps();
  }

  get apps$(): Observable<InternalMarketplaceApp[]> {
    return this.internalMarketplaceApps$;
  }

  get appsForSelectedMarket(): Observable<InternalMarketplaceApp[]> {
    return combineLatest([
      this.apps$,
      this.marketsService.currentMarket$.pipe(
        switchMap((market) =>
          this.vconfigService.getConfigurations(this.partnerId, ['rm_name', 'sm_name', 'ms_name'], market.market_id),
        ),
      ),
    ]).pipe(
      map(([apps, config]) => this.whitelabelVendastaApps(apps, config)),
      shareReplay(1),
    );
  }

  private get partnerId(): string {
    return this.partnerService.partnerId;
  }

  private internalMarketplaceApps$$: ReplaySubject<InternalMarketplaceApp[]> = new ReplaySubject<
    InternalMarketplaceApp[]
  >();
  private internalMarketplaceApps$: Observable<InternalMarketplaceApp[]> =
    this.internalMarketplaceApps$$.asObservable();

  static internalAppFromSDKApp(app: MarketplaceApp): InternalMarketplaceApp {
    app.endUserMarketing = app.endUserMarketing || ({} as MarketingInformation);
    return new InternalMarketplaceApp({
      appId: app.appId,
      name: app.name,
      type: app.type || AppType.APP_TYPE_APP,
      vendor: app.integration ? app.integration.vendor : '',
      price: app.price,
      setupFee: app.setupFee,
      allowPriceNegotiation: app.allowPriceNegotiation,
      currency: app.currency,
      billingFrequency: app.billingFrequency,
      tagline: app.tagline,
      lmiCategories: app.lmiCategories ? app.lmiCategories.map((lmi) => ENUM_TO_LMI_ARRAY[lmi.valueOf()].name) : [],
      icon: app.icon,
      headerImage: app.headerImage,
      screenshot: app.screenshot,
      orderForm: app.orderForm ? app.orderForm.orderForm : null,
      commonForm: app.orderForm ? app.orderForm.commonForm : null,
      integration: app.integration,
      suspended: app.suspended,
      usesCustomPricing: app.usesCustomPricing,

      // Reseller marketing
      description: app.resellerMarketing ? app.resellerMarketing.description : '',
      activationMessage: app.orderForm ? app.orderForm.activationMessage : '',
      _keySellingPoints: app.resellerMarketing ? app.resellerMarketing.keySellingPoints : [],
      restrictions: app.restrictions,
      usesOrderForm: app.usesOrderForm,
      usesActivationSpecificEntryUrl: app.usesActivationSpecificEntryUrl,
      activationRequiresApproval: app.integration ? app.integration.activationRequiresApproval : false,
      billingId: app.billingId,

      // end user marketing
      endUserMarketing: {
        description: app.endUserMarketing.description,
        keySellingPoints: app.endUserMarketing.keySellingPoints,
        faqs: (app.endUserMarketing.faqs || []).map((f) => {
          return { question: f.question, answer: f.answer };
        }),
        files: app.endUserMarketing.files || [],
      },
      allowMultipleActivations: app.multipleActivations,
      usesEditions: app.usesEditions,
      editionIds: app.editionIds,
      suggestedRetailPrice: app.suggestedRetailPrice,
      trialConfiguration: app.trialConfiguration,
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
  }

  loadAllMarketplaceApps(): void {
    const appsFieldMask = new FieldMask();
    appsFieldMask.paths = [
      'appId',
      'version',
      'name',
      'icon',
      'allowWhitelabel',
      'inDevelopment',
      'trialSupport',
      'usesOrderForm',
      'tagline',
      'signInUrl',
      'currency',
      'price',
      'billingFrequency',
      'serviceModel',
      'screenshot',
      'websiteUrl',
      'headerImage',
      'productCategory',
      'lmiCategories',
      'orderForm',
      'created',
      'updated',
      'restrictions',
      'billingId',
      'gdprCompliant',
      'preventPriceConversionBetweenCurrencies',
      'suggestedRetailPrice',
      'integration',
      'multipleActivations',
      'usesActivationSpecificEntryUrl',
      'usesEditions',
      'editionIds',
      'suspended',
      'usesCustomPricing',
      'setupFee',
    ];
    this.marketsService.currentMarket$
      .pipe(
        switchMap((market) =>
          this.pkgAppsService.getAllDistributedAppIds(market.partner_id, market.market_id, [
            MarketplacePackagesAppType.APP,
          ]),
        ),
        switchMap((IDs) => this.marketPlaceApiService.getMultiApps(IDs, appsFieldMask)),
        map((apps) => apps.filter(Boolean)),
        map((apps) => apps.map(MarketplaceAppService.internalAppFromSDKApp)),
        take(1),
      )
      .subscribe((apps) => this.internalMarketplaceApps$$.next(apps));
  }

  getAppWithId(appId: string): Observable<MarketplaceApp> {
    return this.marketPlaceApiService.getApp(appId);
  }

  getProductFromMarketplaceApp(app: MarketplaceApp, isEnabled: boolean, addons: Addon[]): frontendProduct {
    const isSuperAdmin = this.configService.config.isSuperAdmin;
    let price = null;
    if (app.price >= 0) {
      price = app.price;
    }
    const output = new frontendProduct({
      productId: app.appId,
      name: app.name,
      billingId: app.billingId,
      description: app.resellerMarketing ? app.resellerMarketing.description : '',
      tagline: app.tagline,
      iconUrl: app.icon,
      headerImageUrl: app.headerImage,
      screenshotUrls: app.screenshot,
      pdfUploadUrls: app.resellerMarketing ? app.resellerMarketing.files : [],
      keySellingPoints: app.resellerMarketing ? app.resellerMarketing.keySellingPoints : [],
      faqs: app.resellerMarketing.faqs ? app.resellerMarketing.faqs : [],
      needHelp: app.resellerMarketing ? app.resellerMarketing.needHelp : '',
      wholesalePrice: price,
      isArchived: !isEnabled,
      isSuspended: !!app.suspended,
      addons: addons,
      restrictions: app.restrictions,
      usesEditions: app.usesEditions,
    });
    output.videos = app.resellerMarketing ? app.resellerMarketing.videos : [];
    output.lmiCategories = app.lmiCategories
      ? app.lmiCategories.map((lmi) => ENUM_TO_LMI_ARRAY[lmi.valueOf()].name)
      : [];
    output.currency = app.currency;
    output.billingFrequency = app.billingFrequency;
    output.pricing = {
      currency: output.currency,
      prices: [
        {
          price: app.price,
          frequency: output.billingFrequency,
        },
      ],
    };
    output.usesCustomPricing = app.usesCustomPricing;
    output.trialConfiguration = app.trialConfiguration as TrialConfigurationInterface;
    output.files = (app.resellerMarketing.files || [])
      .map((fileUrl) => new File(fileUrl))
      .filter((curFile) => {
        // TODO: Temporary hack to hide battlesheets from non-vendasta users; once the platform has a concept
        // of view permissions for files (or at least a separate set of files for this purpose) on the marketing
        // data we can implement this properly, and rip this hack out.
        if (!curFile || !curFile.name) {
          return false;
        } else if (!isSuperAdmin && curFile.name.toLowerCase().includes('[v]')) {
          return false;
        }
        return true;
      });

    return output;
  }

  private whitelabelVendastaApps(apps: InternalMarketplaceApp[], config: any): InternalMarketplaceApp[] {
    return apps.map((app) => {
      if (!!config.rm_name && app.appId === 'RM') {
        app.name = config.rm_name;
      } else if (!!config.sm_name && app.appId === 'SM') {
        app.name = config.sm_name;
      } else if (!!config.ms_name && app.appId === AppIDs.ListingBuilder) {
        app.name = config.ms_name;
      }
      return app;
    });
  }

  enableAppAndAddons(appId: string, origin: string, requiredAppIds?: string[]): Observable<null> {
    const enableApp$ = this.partnerId$.pipe(
      switchMap((partnerId) => this.enablementService.enableAppForPartner(partnerId, appId, origin)),
    );

    // TODO - write a workflow in the backend that triggers the enablement of the required products (for both apps and addons).
    const enableRequirements$ = this.partnerId$.pipe(
      switchMap((partnerId) =>
        requiredAppIds?.length > 0
          ? zip(...requiredAppIds.map((rai) => this.enablementService.enableAppForPartner(partnerId, rai, origin)))
          : of([]),
      ),
    );

    const addons$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.addonService.getAllAddons(partnerId, [appId], GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES);
      }),
    );

    return combineLatest([this.partnerId$, addons$, enableApp$, enableRequirements$]).pipe(
      take(1),
      tap(
        ([partnerId, addons]) => {
          const enabledIds = [appId, ...(requiredAppIds ?? []), ...addons.map((a) => a.addonId)];
          enabledIds.forEach((ei) => {
            this.productChangeService.changePartnerAppEnablementStatus(partnerId, ei, AppEnablementStatus.ENABLED);
          });
        },
        () => {
          this.snackbarService.errorSnack(
            this.translationService.instant('MARKETPLACE.DISCOVERABLE_ITEM.ENABLE_FAILED'),
          );
        },
      ),
      map(() => null),
    );
  }
}
